import { Pan, Zoom, Bounds } from '../diagram.types';
import { Ref, useState, useRef } from 'preact/hooks';
import { useDragPan } from './useDragPan';
import { useWheelZoom } from './useWheelZoom';
import { usePinchZoom } from './usePinchZoom';

import {
  centerXYToPanXY,
  getPanProps,
  getZoomProps,
  getZoomToFitZoom
} from '../utils/diagramUtils';
import { PanBounds, getPanBounds, pan } from '../utils/panUtils';
import { zoom } from '../utils/zoomUtils';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';

import { Property } from 'csstype';

type PanZoomProps = {
  props: Pan & Zoom;
  captureTargetRef: Ref<HTMLDivElement>;
  width: number;
  height: number;
  contentBounds: Bounds;
};

export const usePanZoom = ({
  props,
  captureTargetRef,
  width,
  height,
  contentBounds
}: PanZoomProps) => {
  const [cursor, setCursor] = useState<Property.Cursor>();
  const zoomProps = getZoomProps(props, width, height, contentBounds);
  const panProps = getPanProps(props, contentBounds);
  const panBounds = useRef<PanBounds>();
  const prevZoom = useRef<number>();
  const panning = useRef<string>();
  const initPanZoomState = useRef({
    zoom: props.zoomValue || 0,
    centerX: props.centerX,
    centerY: props.centerY
  });
  if (
    contentBounds.w !== Infinity &&
    (zoomProps.zoomValue !== prevZoom.current || panProps.panning !== panning.current)
  ) {
    panBounds.current = getPanBounds(
      width,
      height,
      contentBounds,
      panProps.panning,
      zoomProps.zoomValue,
      zoomProps.minZoom,
      initPanZoomState.current
    );
    prevZoom.current = zoomProps.zoomValue;
    panning.current = panProps.panning;
  }
  const dragPanProps = useDragPan({
    panProps: panProps,
    captureTargetRef: captureTargetRef,
    panBounds: panBounds.current,
    zoom: zoomProps.zoomValue,
    width,
    height,
    onDragPanStart: () => setCursor('grabbing'),
    onDragPanEnd: () => setCursor('auto')
  });
  //dZoom or nZoom is required
  const kbZoomCallback = (props: { dZoom?: number; nZoom?: number }) => {
    const newZoom = props.dZoom
      ? zoomProps.zoomValue + props.dZoom
      : getZoomToFitZoom(width, height, contentBounds);
    zoom(
      props.nZoom ? props.nZoom : newZoom,
      zoomProps.zoomValue,
      zoomProps.minZoom,
      zoomProps.maxZoom,
      zoomProps.onZoom
    );
  };
  const prevPanState = useRef({ panX: 0, panY: 0 });

  // dPan or nPan is required
  const kbPanCallback = (props: {
    dPan?: { dx: number; dy: number };
    nPan?: { cx: number; cy: number };
    center?: boolean;
  }) => {
    let centerX;
    let centerY;
    if (props.center) {
      centerX = contentBounds.x + contentBounds.w / 2;
      centerY = contentBounds.y + contentBounds.h / 2;
    } else if (props.dPan) {
      centerX = panProps.centerX + props.dPan.dx;
      centerY = panProps.centerY + props.dPan.dy;
    } else {
      centerX = props.nPan!.cx;
      centerY = props.nPan!.cy;
    }
    const panValues = centerXYToPanXY(centerX, centerY, zoomProps.zoomValue, width, height);
    const { newPan } = pan(
      prevPanState.current,
      panValues,
      panProps.panDirection,
      zoomProps.zoomValue,
      width,
      height,
      panBounds.current,
      panProps.onPan
    );
    prevPanState.current = newPan;
  };
  const wheelZoomProps = useWheelZoom(zoomProps);
  const pinchZoomProps = usePinchZoom({
    captureTargetRef,
    zooming: zoomProps.zooming,
    minZoom: zoomProps.minZoom,
    maxZoom: zoomProps.maxZoom,
    zoomValue: zoomProps.zoomValue,
    onZoom: zoomProps.onZoom
  });
  const panZoomProps = mergeProps(dragPanProps, wheelZoomProps, pinchZoomProps);

  return {
    panZoomProps,
    cursor,
    centerX: panProps.centerX,
    centerY: panProps.centerY,
    zoomValue: zoomProps.zoomValue,
    kbPanCallback: panProps.panning !== 'off' ? kbPanCallback : undefined,
    kbZoomCallback: zoomProps.zooming !== 'off' ? kbZoomCallback : undefined
  };
};
